<template>
  <div v-if="opened" class="rounded-md p-4" :class="`bg-${color}-50`">
    <div class="flex">
      <div class="shrink-0">
        <component :is="icon" :class="`h-5 w-5 text-${color}-400`" />
      </div>
      <div class="ml-3" :class="{ 'flex-1 md:flex md:justify-between': linkName }">
        <span
          v-if="title || $slots.default"
          class="text-sm font-medium"
          :class="`text-${color}-800`"
        >
          <slot>{{ title }}</slot>
        </span>
        <div
          v-if="description || $slots.description"
          class="mt-2 text-sm"
          :class="`text-${color}-700`"
        >
          <slot name="description">
            <p>{{ description }}</p>
          </slot>
        </div>
        <div v-else-if="list.length > 0" class="mt-2 text-sm" :class="`text-${color}-700`">
          <ul class="list-disc pl-5 space-y-1">
            <li v-for="item in list" :key="item">
              {{ item }}
            </li>
          </ul>
        </div>
        <p v-if="linkName" class="mt-2 text-sm md:mt-0 md:ml-6">
          <component
            :is="linkAs"
            :href="linkTarget"
            :target="linkTargetType"
            :class="`whitespace-nowrap font-medium text-${color}-700 hover:text-${color}-600`"
          >
            {{ linkName }} <span aria-hidden="true">&rarr;</span>
          </component>
        </p>
        <p v-if="actionName" class="mt-2 text-sm">
          <a
            :class="`cursor-pointer whitespace-nowrap font-medium text-${color}-700 hover:text-${color}-600 flex items-center`"
            @click="$emit('click')"
            ><component :is="actionIcon" class="h-4 w-4 mr-2" />{{ actionName }}</a
          >
        </p>
      </div>
      <!-- Close button -->
      <div v-if="showCloseButton" class="ml-auto pl-3">
        <div class="-mx-1.5 -my-1.5">
          <button
            type="button"
            :class="`inline-flex bg-${color}-50 rounded-md p-1.5 text-${color}-500 hover:bg-${color}-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-${color}-50 focus:ring-${color}-600`"
            @click="close"
          >
            <span class="sr-only">Dismiss</span>
            <SolidXIcon class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
const colors = {
  error: 'red',
  warning: 'yellow',
  info: 'blue',
  success: 'green',
}
const icons = {
  error: 'SolidXCircleIcon',
  warning: 'SolidExclamationIcon',
  info: 'SolidInformationCircleIcon',
  success: 'SolidCheckCircleIcon',
}
export default {
  name: 'Alert',
  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    list: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => {
        return ['info', 'warning', 'error', 'success'].includes(value)
      },
    },
    closable: {
      type: Boolean,
      default: true,
    },
    linkName: {
      type: String,
      default: '',
    },
    linkAs: {
      type: String,
      default: 'a',
    },
    linkTarget: {
      type: String,
      default: '',
    },
    linkTargetType: {
      type: String,
      default: '_self',
    },
    actionName: {
      type: String,
      default: '',
    },
    actionIcon: {
      type: String,
      default: 'LinkIcon',
    },
  },
  emits: ['close', 'click'],
  setup(props, context) {
    const opened = ref(true)
    const color = computed(() => {
      return colors[props.type]
    })
    const icon = computed(() => {
      return icons[props.type]
    })
    const showCloseButton = computed(() => {
      return !props.linkName && props.closable
    })
    const close = () => {
      context.emit('close')
      opened.value = false
    }
    return {
      opened,
      close,
      color,
      icon,
      showCloseButton,
    }
  },
}
</script>
